import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, X, Search, Book, ShoppingCart, Heart, Folder } from "react-feather";
import { ShopDaysModal, type ShopDaysModalRef } from "../components/ShopDaysModal";
import { StorageImage } from "../components/StorageImage";
import { useToast } from "../components/Toast";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  MEAL_SLOTS,
  MEAL_SLOT_LABEL_KEYS,
  type MealSlot,
  type Recipe,
  recipeImageUri,
  addPlannedMeal,
  removePlannedMeal,
  movePlannedMeal,
  selectAllPlannedMeals,
  selectAllRecipes,
  selectWeekStartsOn,
  selectCollectionSummaries,
  selectRecipesByCollection,
  compareRecipeTitles,
  selectLocale,
  intlLocale,
  useT,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";

// --- date helpers (local calendar, "YYYY-MM-DD") ---
const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfWeek = (d: Date, weekStartsOn: number) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() - weekStartsOn + 7) % 7));
  return x;
};

// Stable empty list so the collection selector doesn't churn refs when the picker
// source isn't a collection.
const EMPTY_RECIPES: Recipe[] = [];
const monthDay = (d: Date, intl: string) =>
  d.toLocaleDateString(intl, { day: "numeric", month: "short" });

function Thumb({ recipe }: { recipe: Recipe }) {
  return (
    <div className="w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-terracotta-wash flex items-center justify-center">
      <StorageImage
        stored={recipeImageUri(recipe)}
        fallback={<Book className="w-4 h-4 text-terracotta-ink/50" />}
      />
    </div>
  );
}

// A slot that recipes can be dropped onto (id = "YYYY-MM-DD|slot").
function DroppableSlot({
  id,
  isFirst,
  onClick,
  children,
}: {
  id: string;
  isFirst: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    // The whole slot is clickable to add a meal (like RN); inner controls stop
    // propagation so they act on their own.
    <div
      ref={setNodeRef}
      onClick={onClick}
      className={`px-4 py-3 transition-colors cursor-pointer ${
        isFirst ? "" : "border-t border-line"
      } ${isOver ? "bg-terracotta/10" : "hover:bg-surface-2"}`}
    >
      {children}
    </div>
  );
}

// A draggable planned meal. Uses a distance-activated sensor (see MealPlan) so a
// plain click still navigates; only a real drag moves it.
function DraggableMeal({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({ id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className="touch-none outline-none"
    >
      {children}
    </div>
  );
}

export default function MealPlan() {
  const t = useT();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const recipes = useAppSelector(selectAllRecipes);
  const allPlanned = useAppSelector(selectAllPlannedMeals);
  const weekStartsOn = useAppSelector(selectWeekStartsOn);
  const intl = intlLocale(useAppSelector(selectLocale));
  const navigate = useNavigate();
  const shopModalRef = useRef<ShopDaysModalRef>(null);

  const [weekOffset, setWeekOffset] = useState(0);
  const [picker, setPicker] = useState<{ date: string; slot: MealSlot } | null>(null);
  const [query, setQuery] = useState("");
  // Which set the picker draws from: "all", "favourites", or a collection id.
  const [pickerSource, setPickerSource] = useState<string>("all");
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const overId = String(over.id);
    const sep = overId.lastIndexOf("|");
    const dayYmd = overId.slice(0, sep);
    const slot = overId.slice(sep + 1) as MealSlot;
    const meal = allPlanned.find((m) => m.id === active.id);
    if (meal && (meal.mealDate !== dayYmd || meal.slot !== slot)) {
      dispatch(movePlannedMeal({ id: String(active.id), mealDate: dayYmd, slot }));
    }
  };

  const todayYmd = ymd(new Date());
  const weekStart = useMemo(
    () => startOfWeek(addDays(new Date(), weekOffset * 7), weekStartsOn),
    [weekOffset, weekStartsOn],
  );
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const weekStartYmd = ymd(days[0]);
  const weekEndYmd = ymd(days[6]);

  const recipesById = useMemo(() => new Map(recipes.map((r) => [r.id, r])), [recipes]);

  const byDaySlot = useMemo(() => {
    const map: Record<string, Partial<Record<MealSlot, typeof allPlanned>>> = {};
    for (const m of allPlanned) {
      if (m.mealDate < weekStartYmd || m.mealDate > weekEndYmd) continue;
      if (!recipesById.has(m.recipeId)) continue;
      ((map[m.mealDate] ??= {})[m.slot] ??= []).push(m);
    }
    for (const day of Object.values(map))
      for (const list of Object.values(day)) list!.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [allPlanned, weekStartYmd, weekEndYmd, recipesById]);

  // Collections with recipes → picker source chips.
  const collections = useAppSelector(selectCollectionSummaries);
  const collectionChips = useMemo(
    () => collections.filter((c) => c.recipeCount > 0),
    [collections],
  );
  const collectionRecipes = useAppSelector((s) =>
    pickerSource !== "all" && pickerSource !== "favourites"
      ? selectRecipesByCollection(s, pickerSource)
      : EMPTY_RECIPES,
  );

  const pickerResults = useMemo(() => {
    const isCollection = pickerSource !== "all" && pickerSource !== "favourites";
    const base = isCollection
      ? collectionRecipes
      : pickerSource === "favourites"
        ? recipes.filter((r) => r.favourite)
        : recipes;
    const q = query.trim().toLowerCase();
    const filtered = q ? base.filter((r) => r.title.toLowerCase().includes(q)) : base;
    // Collections keep their manual (drag) order; All / Favourites are alphabetical.
    return isCollection
      ? filtered
      : [...filtered].sort((a, b) => compareRecipeTitles(a.title, b.title));
  }, [recipes, collectionRecipes, pickerSource, query]);

  // Open the picker for a slot, resetting the browse source + search each time.
  const openSlotPicker = (date: string, slot: MealSlot) => {
    setPickerSource("all");
    setQuery("");
    setPicker({ date, slot });
  };

  const addToSlot = (recipeId: string) => {
    if (!picker) return;
    dispatch(addPlannedMeal({ recipeId, mealDate: picker.date, slot: picker.slot }));
    toast.show(
      t("mealPlan.addedToSlot", {
        slot: t(MEAL_SLOT_LABEL_KEYS[picker.slot]),
        date: monthDay(new Date(picker.date + "T00:00:00"), intl),
      }),
    );
    setPicker(null);
    setQuery("");
  };

  const weekLabel = `${monthDay(days[0], intl)} – ${monthDay(days[6], intl)}`;
  const activeMeal = activeId ? allPlanned.find((m) => m.id === activeId) : null;
  const activeRecipe = activeMeal ? recipesById.get(activeMeal.recipeId) : null;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl font-extrabold text-ink">{t("nav.mealPlanner")}</h1>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeekOffset((o) => o - 1)}
            aria-label={t("mealPlan.previousWeek")}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-surface border border-line text-ink-soft hover:bg-surface-2 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className="min-w-[130px] text-center cursor-pointer"
          >
            <span className="block text-sm font-bold text-ink">{weekLabel}</span>
            <span className="block text-xs font-semibold text-terracotta-ink">
              {weekOffset === 0 ? t("mealPlan.thisWeek") : t("mealPlan.backToThisWeek")}
            </span>
          </button>
          <button
            onClick={() => setWeekOffset((o) => o + 1)}
            aria-label={t("mealPlan.nextWeek")}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-surface border border-line text-ink-soft hover:bg-surface-2 cursor-pointer"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mb-5">
        <button
          onClick={() => shopModalRef.current?.open()}
          className="flex items-center gap-1.5 text-sm font-semibold text-terracotta-ink hover:text-terracotta-dark cursor-pointer"
        >
          <ShoppingCart className="w-4 h-4" />
          {t("recipe.addToShoppingList")}
        </button>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex flex-col gap-5">
          {days.map((day) => {
            const dayYmd = ymd(day);
            const isToday = dayYmd === todayYmd;
            const isPast = dayYmd < todayYmd;
            return (
              <div key={dayYmd}>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`text-base font-bold ${
                      isToday ? "text-terracotta-ink" : isPast ? "text-ink-faint" : "text-ink"
                    }`}
                  >
                    {day.toLocaleDateString(intl, { weekday: "long" })}
                  </span>
                  <span className="text-sm text-ink-faint">{monthDay(day, intl)}</span>
                  {isToday && <span className="w-1.5 h-1.5 rounded-full bg-terracotta" />}
                </div>

                <div className="rounded-2xl bg-surface border border-line overflow-hidden">
                  {MEAL_SLOTS.map((slot, i) => {
                    const items = byDaySlot[dayYmd]?.[slot] ?? [];
                    return (
                      <DroppableSlot
                        key={slot}
                        id={`${dayYmd}|${slot}`}
                        isFirst={i === 0}
                        onClick={() => openSlotPicker(dayYmd, slot)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-ink-faint">
                            {t(MEAL_SLOT_LABEL_KEYS[slot])}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openSlotPicker(dayYmd, slot);
                            }}
                            className="flex items-center gap-1 text-[13px] font-semibold text-terracotta-ink hover:text-terracotta-dark cursor-pointer"
                          >
                            <Plus className="w-4 h-4" strokeWidth={2.5} />
                            {t("common.add")}
                          </button>
                        </div>

                        {items.map((m) => {
                          const recipe = recipesById.get(m.recipeId)!;
                          return (
                            <DraggableMeal key={m.id} id={m.id}>
                              <div className="flex items-center gap-2 mt-3">
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/recipe/${recipe.id}`);
                                  }}
                                  className="group flex items-center gap-3 flex-1 min-w-0 cursor-grab active:cursor-grabbing"
                                >
                                  <Thumb recipe={recipe} />
                                  <span className="flex-1 min-w-0 text-[15px] font-semibold text-ink line-clamp-2 group-hover:text-terracotta-ink transition-colors">
                                    {recipe.title}
                                  </span>
                                </div>
                                <button
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch(removePlannedMeal(m.id));
                                  }}
                                  aria-label={t("common.remove")}
                                  className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-ink-faint hover:text-ink hover:bg-surface-2 cursor-pointer"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </DraggableMeal>
                          );
                        })}
                      </DroppableSlot>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <DragOverlay>
          {activeRecipe ? (
            <div className="flex items-center gap-3 bg-surface border border-line rounded-xl px-3 py-2 shadow-lg">
              <Thumb recipe={activeRecipe} />
              <span className="text-[15px] font-semibold text-ink line-clamp-1">
                {activeRecipe.title}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Recipe picker */}
      {picker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPicker(null)}
        >
          <div
            className="w-full max-w-md max-h-[80vh] flex flex-col rounded-2xl bg-surface p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-xl font-bold text-ink">
                  {t("mealPlan.addToSlot", { slot: t(MEAL_SLOT_LABEL_KEYS[picker.slot]) })}
                </h2>
                <p className="text-sm text-ink-faint">
                  {new Date(picker.date + "T00:00:00").toLocaleDateString(intl, {
                    weekday: "long",
                    day: "numeric",
                    month: "short",
                  })}
                </p>
              </div>
              <button
                onClick={() => setPicker(null)}
                aria-label={t("common.close")}
                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-ink-faint hover:text-ink hover:bg-surface-2 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Source chips: All / Favourites / each collection. List is always A–Z.
                shrink-0 stops the flex column from squeezing the row; items-center
                keeps the pills at their natural height instead of stretching tall. */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-3 shrink-0">
              {[
                { key: "all", label: t("mealPlan.all") },
                { key: "favourites", label: t("nav.favourites") },
                ...collectionChips.map((c) => ({ key: c.id, label: c.name })),
              ].map((chip) => {
                const active = pickerSource === chip.key;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setPickerSource(chip.key)}
                    className={`shrink-0 flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors cursor-pointer ${
                      active
                        ? "bg-terracotta text-white"
                        : "bg-surface-2 text-ink-soft hover:text-ink"
                    }`}
                  >
                    {chip.key === "favourites" && (
                      <Heart
                        className={`w-3.5 h-3.5 ${active ? "fill-current" : ""}`}
                      />
                    )}
                    {chip.key !== "all" && chip.key !== "favourites" && (
                      <Folder className="w-3.5 h-3.5" />
                    )}
                    {chip.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 bg-surface-2 rounded-xl px-3 py-2.5 mb-3">
              <Search className="w-4 h-4 text-ink-faint shrink-0" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("mealPlan.filterByName")}
                className="flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
              />
            </div>

            <div className="flex-1 overflow-y-auto -mx-2 px-2">
              {pickerResults.length === 0 ? (
                <p className="text-sm text-ink-faint text-center py-8">
                  {recipes.length ? t("mealPlan.noRecipesMatch") : t("mealPlan.noRecipesYet")}
                </p>
              ) : (
                pickerResults.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => addToSlot(r.id)}
                    className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-surface-2 rounded-lg px-2 cursor-pointer"
                  >
                    <Thumb recipe={r} />
                    <span className="flex-1 min-w-0 text-[15px] font-semibold text-ink line-clamp-2">
                      {r.title}
                    </span>
                    <Plus className="w-5 h-5 text-terracotta-ink shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <ShopDaysModal
        ref={shopModalRef}
        days={days}
        todayYmd={todayYmd}
        onAdded={() => navigate("/shopping-list")}
      />
    </div>
  );
}
